import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/button";
import { Input } from "@/components/input";
import { StatusCard } from "@/components/status-card";
import { resolveLocation, getAllKommuner, getKommunStatusByKod } from "@/lib/api";
import type { Kommun } from "@/lib/api";
import type { KommunStatus } from "@/lib/status";
import { Crosshair, Loader2, MapPin, Search } from "lucide-react";
import { toast } from "sonner";

type ResolveResult = {
  kommun_namn: string | null;
  kommunkod: string | null;
  status: KommunStatus[];
};

const STORAGE_KEY = "bk:last-location";

export function LocationPrompt() {
  const [result, setResult] = useState<ResolveResult | null>(null);
  const [denied, setDenied] = useState(false);

  // Restore cached lookup on mount
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) setResult(JSON.parse(cached));
    } catch (e: unknown) {
      console.error('Session storage error:', e);
    }
  }, []);

  const resolveMut = useMutation({
    mutationFn: async (coords: { lat: number; lng: number }) => await resolveLocation(coords.lat, coords.lng),
    onSuccess: (r) => {
      setResult(r);
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(r));
      } catch (e: unknown) {
        console.error('Session storage error:', e);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const requestLocation = () => {
    if (!("geolocation" in navigator)) {
      setDenied(true);
      toast.error("Din webbläsare stödjer inte platsdelning");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolveMut.mutate({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }),
      (err) => {
        console.warn(err);
        setDenied(true);
        toast.error(
          err.code === err.PERMISSION_DENIED
            ? "Du nekade platsdelning. Sök på kommun istället."
            : "Kunde inte hämta din position."
        );
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  // Simple search using the kommuner table (one row per kommun)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Kommun[]>([]);
  const [allKommuner, setAllKommuner] = useState<Kommun[]>([]);

  // Load all kommuner for search
  useEffect(() => {
    getAllKommuner().then(setAllKommuner).catch(console.error);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const q = searchQuery.toLowerCase();
    const filtered = allKommuner.filter(k =>
      k.namn.toLowerCase().includes(q) ||
      k.kommunkod.toLowerCase().includes(q)
    ).slice(0, 10);
    setSearchResults(filtered);
  }, [searchQuery, allKommuner]);

  const handleSelectKommun = async (kommun: Kommun) => {
    let status: KommunStatus[] = [];
    try {
      status = await getKommunStatusByKod(kommun.kommunkod);
    } catch (e: unknown) {
      console.error('Failed to fetch kommun status:', e);
      toast.error("Kunde inte hämta status för kommunen");
      return;
    }
    const result: ResolveResult = {
      kommun_namn: kommun.namn,
      kommunkod: kommun.kommunkod,
      status,
    };
    setResult(result);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(result));
    setSearchQuery("");
    setSearchResults([]);
  };

  const reset = () => {
    setResult(null);
    sessionStorage.removeItem(STORAGE_KEY);
    setDenied(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  if (result) {
    return (
      <div className="space-y-4">
        <StatusCard
          kommunNamn={result.kommun_namn ?? "Okänd plats"}
          data={result.status}
          unmatched={result.status.length === 0}
        />
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={reset}>
            Sök en annan plats
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 sm:p-10 shadow-sm">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          <MapPin className="h-3.5 w-3.5" /> Hitta status där du är
        </span>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-foreground">
          Är det bevattningsförbud
          <br />
          <span className="text-primary">där du är?</span>
        </h1>
        <p className="mt-4 text-base text-muted-foreground sm:text-lg">
          Bevattningskartan samlar information om aktuella bevattningsförbud och
          rekommendationer från Sveriges VA-huvudmän. Dela din position eller
          skriv din kommun för att se aktuell status.
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button
            size="lg"
            onClick={requestLocation}
            disabled={resolveMut.isPending}
            className="gap-2"
          >
            {resolveMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Crosshair className="h-4 w-4" />
            )}
            Använd min plats
          </Button>
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="…eller sök på kommun"
              className="h-11 pl-9"
              aria-label="Sök kommun"
            />
            {searchResults.length > 0 && (
              <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-lg border border-border bg-popover shadow-lg">
                {searchResults.map((kommun) => (
                  <li key={kommun.kommunkod}>
                    <button
                      type="button"
                      onClick={() => handleSelectKommun(kommun)}
                      className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-accent"
                    >
                      <span className="font-medium">{kommun.namn}</span>
                      <span className="text-xs text-muted-foreground">
                        {kommun.lan}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {denied && (
          <p className="mt-4 text-sm text-muted-foreground">
            Vi sparar inte din plats — sökningen sker bara här i din webbläsare.
          </p>
        )}
      </div>
    </div>
  );
}
